'use client';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useMemo, useRef } from 'react';
import { IDeviceState } from '~/helper/read-device-from-headers';
import { useScreen } from '~/hooks/use-screen';

export type MessagingEnvKey =
  | 'uat.mattressfirm.com'
  | 'dev-sit.mattressfirm.com'
  | 'www.mattressfirm.com';

export type MessagingConfig = {
  ORG_ID: string;
  SITE_URL: string;
  SCRT_URL: string;
};

export type ChatBotScriptProps = IDeviceState & {
  currentDomain: MessagingEnvKey;
};

const envConfigs: Partial<Record<MessagingEnvKey, MessagingConfig>> = {
  'uat.mattressfirm.com': {
    ORG_ID: '00D790000000Mwj',
    SITE_URL:
      'https://mattress--uat.sandbox.my.site.com/ESWMfWChat1755279332568',
    SCRT_URL: 'https://mattress--uat.sandbox.my.salesforce-scrt.com',
  },
  'dev-sit.mattressfirm.com': {
    ORG_ID: '00D3J0000008lg4',
    SITE_URL:
      'https://mattress--qa.sandbox.my.site.com/ESWMfWChat1721768781975',
    SCRT_URL: 'https://mattress--qa.sandbox.my.salesforce-scrt.com',
  },
  'www.mattressfirm.com': {
    ORG_ID: '00DG0000000gBKA',
    SITE_URL: 'https://mattress.my.site.com/ESWMfWChat1755873710209',
    SCRT_URL: 'https://mattress.my.salesforce-scrt.com',
  },
};

/** Chat script with custom widget */
export function buildMessagingScript(cfg: MessagingConfig): string {
  return `
    var ORG_ID='${cfg.ORG_ID}';
    var DEPLOY   = 'MfW_Chat';
    var SITE_URL = '${cfg.SITE_URL}';
    var SCRT_URL = '${cfg.SCRT_URL}';

    // Generic Constant
    var OPEN_EVENTS  = ['OPENED','VISIBLE','EXPANDED','ENGAGED','SESSION_STARTED'];
    var CLOSE_EVENTS = ['CLOSED','HIDDEN','MINIMIZED','SESSION_ENDED','DEFAULT','IDLE'];
    var READY_GRACE_MS = 450;   // short window to catch auto-restore
    var openedSeen = false;

    /* Helpers */
    function onReady(fn){ if(document.readyState!=='loading'){ fn(); } else { document.addEventListener('DOMContentLoaded', fn); } }
    function setBtnVisible(show){
      var b=document.getElementById('customChatButton');
      if(!b) return;
      b.style.display = show ? 'flex' : 'none';
    }

    function hideSfLauncher(){
      if (window.embeddedservice_bootstrap && window.embeddedservice_bootstrap.utilAPI
          && typeof embeddedservice_bootstrap.utilAPI.hideChatButton === 'function') {
        embeddedservice_bootstrap.utilAPI.hideChatButton();
      }
    }

    /* --------------- */
    /* Styles + Button */
    /* --------------- */
    function injectStylesAndButton(){
      var css = ''
        + '#customChatButton{position:fixed;bottom:53px;right:28px;z-index:1000;'
        + 'background:#d63426;color:#fff;font-family:Rubik,Arial,sans-serif;'
        + 'font-size:13px;font-weight:500;line-height:1.0;border-radius:30px;'
        + 'padding:10px 20px;display:none;height:60px;width:164px;overflow:visible;'
        + 'border:2px solid #fff;cursor:pointer;-webkit-tap-highlight-color:transparent;}'
        + '#customChatButtonInner{display:flex;align-items:center;justify-content:center;width:100%;height:100%}'
        + '#customChatButtonInner span{width:104px;display:block;margin-left:-16px;text-align:left;user-select:none}'
        + '.chatFloat{position:absolute;top:-10px;right:-18px;width:60px;height:60px;border-radius:50%;pointer-events:none}'
        + '.chatFloat svg{width:100%;height:100%;background-image:url(https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAFA2J?pubver=0);'
        + 'background-size:cover;border-radius:50%;border:2px solid #fff}'
        + '#embeddedMessagingConversationButton{display:none !important}';
      var style=document.createElement('style'); style.textContent=css; document.head.appendChild(style);

      if(!document.getElementById('customChatButton')){
        var btn=document.createElement('div');
        btn.id='customChatButton'; btn.setAttribute('role','button'); btn.setAttribute('tabindex','0');
        var html = ''
          + '<div id="customChatButtonInner">'
          +   '<div class="chatFloat"><svg xmlns="http://www.w3.org/2000/svg" width="60" height="60"></svg></div>'
          +   '<span>Chat with a Sleep Expert®</span>'
          + '</div>';
        btn.innerHTML = html;
        document.body.appendChild(btn);
      }
    }

    /* -------------------------- */
    /* Tealium tracking functions */
    /* -------------------------- */
  
    function normalizeEmail(email) {
      return email ? email.toLowerCase() : '';
    }

    function normalizePhone(phone) {
      if (!phone?.trim()) return '';

      // Remove all non-numeric characters
      let digits = phone.replace(/\D/g, '');

      // Ensure it starts with "1"
      if (!digits.startsWith('1')) {
        digits = '1' + digits;
      }

      return digits;
    }

    function sales(email, phone) {
      const normalizedEmail = normalizeEmail(email);
      const normalizedPhone = normalizePhone(phone);

      utag.link({
        event: 'chat-click-sales-op',
        event_type: 'chat_sales_opportunity',
        event_category: 'chat',
        event_action: 'start chatting button click',
        event_label: 'chat sales opportunity',
        ...(normalizedEmail ? { customer_email: normalizedEmail } : {}),
        ...(normalizedPhone ? { customer_phone_number: normalizedPhone } : {})
      });
    }
 
    function service(email, phone) {
      const normalizedEmail = normalizeEmail(email);
      const normalizedPhone = normalizePhone(phone);

      utag.link({
        event: 'chat-click-service-op',
        event_type: 'chat_service_opportunity',
        event_category: 'chat',
        event_action: 'start chatting button click',
        event_label: 'chat service opportunity',
        ...(normalizedEmail ? { customer_email: normalizedEmail } : {}),
        ...(normalizedPhone ? { customer_phone_number: normalizedPhone } : {})
      });
    } 

    /* Combined listener for Salesforce Embedded Service events */
    window.addEventListener('message', function receiveMessage(event) {
      if (!event.data) return;

	  //skip event if fired from SE button
      if (window.suppressDoubleTealiumEvent) {
        return;
      }

      // Custom event (Service/Sales)
      if (event.data.type === 'customEvent' && event.data.data) {
        var chatType = event.data.data.v3; // "Service" or "Sales"
        var email = event.data.data.v1 || '';
        var phone = event.data.data.v2 || '';

        if (chatType === 'Service') {
          console.log('Service chat clicked');
          service(email, phone);
        } else if (chatType === 'Sales') {
          console.log('Sales chat clicked');
          sales(email, phone);
        }
      }

      // Widget load/init event
      if (event.data.type === 'loadEvent') {
        utag.link({
          event: 'chat-widget-initiated',
          event_type: 'chat_click',
          event_category: 'chat',
          event_action: 'click',
          event_label: 'chat engagement'
        });
      }
    }, false);

    /* -------------- */
    /* Messaging init */
    /* -------------- */

    function initMessaging(){
      // Start hidden for Salesforce launcher
      if (window.embeddedservice_bootstrap && window.embeddedservice_bootstrap.settings) {
        embeddedservice_bootstrap.settings.language='en_US';
        embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;
      }

      embeddedservice_bootstrap.init(ORG_ID, DEPLOY, SITE_URL, { scrt2URL: SCRT_URL });

      // SDK ready -> hide OOTB again + show custom button
      window.addEventListener('onEmbeddedMessagingReady', function(){
        hideSfLauncher();
        // To hide on load need to set Button False
        setBtnVisible(false);
        setTimeout(function(){
          if (!openedSeen) setBtnVisible(true);
        }, READY_GRACE_MS);
      });

      // Opened (fresh or restored)
      window.addEventListener('onEmbeddedMessagingConversationOpened', function(){
        openedSeen = true;
        setBtnVisible(false);
      });

      window.addEventListener('onEmbeddedMessagingConversationClosed', function(){
        openedSeen = false;
        hideSfLauncher();
      });

      // Show/hide our button on state changes
      window.addEventListener('onEmbeddedMessagingStateChanged', function(e){
        var s = (e && e.detail && (e.detail.state || e.detail.uiState)) || (e && e.data && e.data.state) || '';
        if (OPEN_EVENTS.indexOf(s) > -1) { openedSeen = true;  setBtnVisible(false); }
        if (CLOSE_EVENTS.indexOf(s) > -1){ openedSeen = false; hideSfLauncher(); setBtnVisible(true); }
      });

      // Explicit close event -> ensure our button returns
      window.addEventListener('onEmbeddedMessagingWindowClosed', function(){
	    openedSeen = false;
        hideSfLauncher();
        setBtnVisible(true);
      });

	  window.addEventListener("onEmbeddedMessagingWindowMinimized", () => {
		setBtnVisible(false);
	  });

	  window.addEventListener("onEmbeddedMessagingWindowMaximized", () => {
		setBtnVisible(false);
	  });

      window.addEventListener('onEmbeddedMessagingButtonCreated', hideSfLauncher);
    }

      /* -------------------------- */
      /* Custom button click wiring */
      /* -------------------------- */

      function wireClickWhenReady(){
      var btn = document.getElementById('customChatButton');
      if(!btn) return;

      function bindOrRetry(){
        var pageLoaded = document.readyState === 'complete';
        var api = window.embeddedservice_bootstrap && window.embeddedservice_bootstrap.utilAPI;
        if(pageLoaded && api && typeof api.launchChat === 'function'){
          btn.addEventListener('click', function(){
            setBtnVisible(false);
            try { api.launchChat(); } catch(e){ console.error('[chat] launchChat failed', e); setBtnVisible(true); }
          }, false);
          btn.addEventListener('keydown', function(e){ if(e.key==='Enter' || e.key===' '){ e.preventDefault(); btn.click(); }}, false);
        } else {
          setTimeout(bindOrRetry, 200);
        }
      }
      bindOrRetry();
    }

      /* ----------------- */
      /* Load SF bootstrap */
      /* ----------------- */

      function loadBootstrap(){
      var s=document.createElement('script');
      s.src = SITE_URL + '/assets/js/bootstrap.min.js';
      s.onload = function(){ initMessaging(); wireClickWhenReady(); };
      s.onerror = function(){ console.error('[chat] bootstrap.min.js failed to load'); };
      document.body.appendChild(s);
    }

    /*------ */
    /* Boot */
    /*---- */

    onReady(function(){
      injectStylesAndButton();
      setBtnVisible(false);
      loadBootstrap();
    });
  `;
}

export const ChatBotScript = ({
  isDesktop,
  currentDomain,
}: ChatBotScriptProps) => {
  const isMobile = useScreen().screenType === 'mobile';
  const pathname = usePathname();
  const hasOpened = useRef(false);
  const isWeekday = useMemo(() => {
    const day = new Date().getDay();

    return [1, 2, 3, 4, 5].includes(day);
  }, []);

  useEffect(() => {
    hasOpened.current = false;
  }, [pathname]);

  useEffect(() => {
    if (isDesktop && isWeekday && !hasOpened.current) {
      const timer = setTimeout(() => {
        const selectors = [
          // SIT/UAT
          'body > div.embedded-messaging > div.embeddedMessagingConversationButtonWrapper > button',
        ];

        const chatButton = selectors
          .map((s) => document.querySelector(s) as HTMLElement)
          .find(Boolean);

        chatButton?.click();
        hasOpened.current = true;
      }, 300000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isDesktop, isWeekday]);

  const cfg = envConfigs[currentDomain as MessagingEnvKey];

  const scriptProps = { id: 'IDNMAa1LZ_cg3nhfb' };

  return (
    <>
      {!isMobile && cfg && (
        <Script {...scriptProps}>{buildMessagingScript(cfg)}</Script>
      )}
    </>
  );
};
